import React from 'react';
import { useQueries } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import { CategoryQueryKeys } from '@/api/queries/categories/categoryQueryKeys';
import {
    useBrandListQuery,
    useBranchDetailsQuery,
    useCategoryPickerQuery,
    useCreateDraftInvoiceMutation,
    useDeleteDraftInvoiceMutation,
    useImportPurchaseMutation,
    useSupplierPickerQuery,
    useUpdateDraftInvoiceMutation,
} from '@/api/queries';
import type { ProductUnit, VatType } from '@/api/models';
import {
    flattenPickerCategories,
    type CategoryFilterOption,
} from '@/pages/products/components/productsMeta';
import {
    buildManualImportRequest,
    computeSummary,
    isDirty,
    makeExpense,
    makeExistingLine,
    makeInitialState,
    makeNewLine,
    syncLinePricing,
    validateManualImport,
    type ExistingLinePick,
    type ImportLineDraft,
    type ManualImportFormState,
    type ManualImportMethod,
    type SalePricingMode,
} from './manualImportMeta';

export type { ExistingLinePick } from './manualImportMeta';

export type SimpleOption = { id: string; name: string };

/** A variation axis (category attribute, role Variation) with its values. */
export type AvailableAxis = {
    attributeTypeId: string;
    name: string;
    values: SimpleOption[];
};

/** Variation axes per category id — lines look up their own category. */
export type VariationAxesByCategory = Record<string, AvailableAxis[]>;

/** Editable free-text/number fields on a line (selects handled separately). */
export type LineStringField =
    | 'name'
    | 'sku'
    | 'barcode'
    | 'quantity'
    | 'purchasePrice'
    | 'salePrice'
    | 'markup'
    | 'wholesalePrice'
    | 'minimumQuantity'
    | 'batchNumber'
    | 'expiryDate'
    | 'saleDueDate';

/** Transient outcome of the last Save/Import action. */
export type ManualImportStatus = 'draftSaved' | 'imported' | null;

/** A failed save/discard/import: which action failed (an i18n wrapper key)
 *  plus the raw server error text, surfaced as-is (backend 400s are English). */
export type ManualImportSaveError = {
    labelKey:
        | 'purchases.manualImport.errors.saveDraftFailed'
        | 'purchases.manualImport.errors.discardDraftFailed'
        | 'purchases.manualImport.errors.importFailed';
    message: string;
};

/** Line fields whose edit re-derives the mode-dependent counterpart. */
const PRICING_FIELDS: ReadonlySet<LineStringField> = new Set([
    'purchasePrice',
    'salePrice',
    'markup',
]);

export type UseManualImportOptions = {
    /** Request method stamped on the assembled payload. Defaults to 'manual';
     *  the file-import page passes 'excel'. */
    method?: ManualImportMethod;
    /** Server id of an already-persisted draft (resume/edit mode). When set,
     *  Save Draft PUTs instead of POSTing. */
    draftId?: string | null;
    /** Called after the first successful save with the server-assigned id —
     *  the page lifts it into the URL so it survives a reload. */
    onDraftCreated?: (draftId: string) => void;
    /** Called after a successful discard (DELETE) — the page navigates away. */
    onDraftDiscarded?: () => void;
    /** Called after a successful final import — the page navigates away. */
    onImported?: () => void;
};

/**
 * Drives the manual-import page. Owns the whole draft (supplier/branch/invoice
 * meta + product lines + extra expenses), derives the picker option lists and
 * the live cost/markup summary, and exposes Save-Draft / Discard / Import.
 *
 * Save Draft persists through `POST/PUT /purchases/draft-invoices` — POST on
 * the first save (the server assigns the id), PUT from then on. Import POSTs
 * `/purchases/import` (with the current `draftId` when set, transitioning
 * the Draft in place to Received) and hands off to `onImported` to leave the
 * page. Server 400 text is surfaced raw via `saveError`. `initialDraft`
 * seeds resume mode and the Excel file-import page.
 */
export const useManualImport = (
    initialDraft?: ManualImportFormState,
    options?: UseManualImportOptions,
) => {
    const method = options?.method ?? 'manual';
    const onDraftCreated = options?.onDraftCreated;
    const onDraftDiscarded = options?.onDraftDiscarded;
    const onImported = options?.onImported;
    const keyRef = React.useRef(1);
    const makeKey = React.useCallback(() => `row-${keyRef.current++}`, []);

    const [state, setState] = React.useState<ManualImportFormState>(
        () => initialDraft ?? makeInitialState(),
    );
    const [status, setStatus] = React.useState<ManualImportStatus>(null);
    const [draftId, setDraftId] = React.useState<string | null>(
        options?.draftId ?? null,
    );
    const [saveError, setSaveError] =
        React.useState<ManualImportSaveError | null>(null);

    const createDraftMutation = useCreateDraftInvoiceMutation();
    const updateDraftMutation = useUpdateDraftInvoiceMutation();
    const deleteDraftMutation = useDeleteDraftInvoiceMutation();
    const importMutation = useImportPurchaseMutation();

    const { apiClient } = useApi();
    const supplierQuery = useSupplierPickerQuery();
    const branchQuery = useBranchDetailsQuery();
    const categoryPickerQuery = useCategoryPickerQuery();
    const brandQuery = useBrandListQuery();

    const supplierOptions = React.useMemo<SimpleOption[]>(() => {
        const result = supplierQuery.data;
        if (!result || !result.success) return [];
        return result.response.items.map((s) => ({ id: s.id, name: s.name }));
    }, [supplierQuery.data]);

    const branchOptions = React.useMemo<SimpleOption[]>(() => {
        const result = branchQuery.data;
        if (!result || !result.success) return [];
        return result.response.items.map((b) => ({ id: b.id, name: b.name }));
    }, [branchQuery.data]);

    const categoryOptions = React.useMemo<CategoryFilterOption[]>(() => {
        const result = categoryPickerQuery.data;
        if (!result || !result.success) return [];
        return flattenPickerCategories(result.response.items);
    }, [categoryPickerQuery.data]);

    const brandOptions = React.useMemo<SimpleOption[]>(() => {
        const result = brandQuery.data;
        if (!result || !result.success) return [];
        return result.response.items.map((b) => ({ id: b.id, name: b.name }));
    }, [brandQuery.data]);

    // Each line shows only the variation attributes its selected category
    // subscribes to — one category-attributes query per distinct category in
    // the draft (cached/shared via the standard key, so two lines with the
    // same category cost one request).
    const lineCategoryIds = React.useMemo(
        () => [
            ...new Set(
                state.lines
                    .map((line) => line.categoryId)
                    .filter((id): id is string => id !== null),
            ),
        ],
        [state.lines],
    );

    const categoryAttributeQueries = useQueries({
        queries: lineCategoryIds.map((categoryId) => ({
            queryKey: CategoryQueryKeys.attributes(categoryId),
            queryFn: () =>
                apiClient.categories.getCategoryAttributes(categoryId),
            staleTime: 60_000,
        })),
    });

    const variationAxesByCategory =
        React.useMemo<VariationAxesByCategory>(() => {
            const map: VariationAxesByCategory = {};
            lineCategoryIds.forEach((categoryId, index) => {
                const result = categoryAttributeQueries[index]?.data;
                if (!result || !result.success) {
                    map[categoryId] = [];
                    return;
                }
                map[categoryId] = result.response.items
                    .filter(
                        (item) =>
                            item.role === 'Variation' &&
                            item.attributeType.values.length > 0,
                    )
                    .sort((a, b) => a.position - b.position)
                    .map((item) => ({
                        attributeTypeId: item.attributeType.id,
                        name: item.attributeType.name,
                        values: item.attributeType.values.map((v) => ({
                            id: v.id,
                            name: v.name,
                        })),
                    }));
            });
            return map;
        }, [categoryAttributeQueries, lineCategoryIds]);

    // Any edit clears the transient saved/imported status and the last
    // save error — both describe a request that no longer matches the form.
    const mutate = React.useCallback(
        (update: (prev: ManualImportFormState) => ManualImportFormState) => {
            setStatus(null);
            setSaveError(null);
            setState(update);
        },
        [],
    );

    const onSupplierChange = React.useCallback(
        (supplierId: string | null) => mutate((p) => ({ ...p, supplierId })),
        [mutate],
    );
    const onBranchChange = React.useCallback(
        (branchId: string | null) => mutate((p) => ({ ...p, branchId })),
        [mutate],
    );
    const onInvoiceNumberChange = React.useCallback(
        (invoiceNumber: string) => mutate((p) => ({ ...p, invoiceNumber })),
        [mutate],
    );
    const onDateChange = React.useCallback(
        (date: string) => mutate((p) => ({ ...p, date })),
        [mutate],
    );

    const onAddExistingLine = React.useCallback(
        (pick: ExistingLinePick) =>
            mutate((p) => ({
                ...p,
                lines: [...p.lines, makeExistingLine(makeKey(), pick)],
            })),
        [makeKey, mutate],
    );

    const onAddNewLine = React.useCallback(
        () =>
            mutate((p) => ({
                ...p,
                lines: [...p.lines, makeNewLine(makeKey())],
            })),
        [makeKey, mutate],
    );

    // Appends an already-shaped line (e.g. a promoted doubtful Excel row).
    const onAddPreparedLine = React.useCallback(
        (prepared: Omit<ImportLineDraft, 'key'>) =>
            mutate((p) => ({
                ...p,
                lines: [...p.lines, { ...prepared, key: makeKey() }],
            })),
        [makeKey, mutate],
    );

    const onRemoveLine = React.useCallback(
        (key: string) =>
            mutate((p) => ({
                ...p,
                lines: p.lines.filter((line) => line.key !== key),
            })),
        [mutate],
    );

    const updateLine = React.useCallback(
        (
            key: string,
            update: (
                line: ImportLineDraft,
                prev: ManualImportFormState,
            ) => ImportLineDraft,
        ) =>
            mutate((p) => ({
                ...p,
                lines: p.lines.map((line) =>
                    line.key === key ? update(line, p) : line,
                ),
            })),
        [mutate],
    );

    const onLineFieldChange = React.useCallback(
        // Editing any pricing field re-derives its dependent counterpart
        // (markup in fixed mode, sale price in markup mode).
        (key: string, field: LineStringField, value: string) =>
            updateLine(key, (line, prev) =>
                PRICING_FIELDS.has(field)
                    ? syncLinePricing(
                          { ...line, [field]: value },
                          prev.pricingMode,
                      )
                    : { ...line, [field]: value },
            ),
        [updateLine],
    );

    const onPricingModeChange = React.useCallback(
        // Values are left untouched on switch — the dependent field re-derives
        // on the next pricing edit, so nothing the user typed is wiped.
        (pricingMode: SalePricingMode) =>
            mutate((p) => ({ ...p, pricingMode })),
        [mutate],
    );
    const onLineCategoryChange = React.useCallback(
        // The category decides which variation axes exist, so picked attribute
        // values are reset alongside it.
        (key: string, categoryId: string | null) =>
            updateLine(key, (line) => ({
                ...line,
                categoryId,
                optionValues: {},
            })),
        [updateLine],
    );
    const onLineBrandChange = React.useCallback(
        (key: string, brandId: string | null) =>
            updateLine(key, (line) => ({ ...line, brandId })),
        [updateLine],
    );
    const onLineOptionChange = React.useCallback(
        (key: string, attributeTypeId: string, valueId: string) =>
            updateLine(key, (line) => ({
                ...line,
                optionValues: {
                    ...line.optionValues,
                    [attributeTypeId]: valueId,
                },
            })),
        [updateLine],
    );
    const onLineUnitChange = React.useCallback(
        (key: string, unit: ProductUnit) =>
            updateLine(key, (line) => ({ ...line, unit })),
        [updateLine],
    );
    const onLineVatChange = React.useCallback(
        (key: string, vatType: VatType) =>
            updateLine(key, (line) => ({ ...line, vatType })),
        [updateLine],
    );

    const onAddExpense = React.useCallback(
        () =>
            mutate((p) => ({
                ...p,
                expenses: [...p.expenses, makeExpense(makeKey())],
            })),
        [makeKey, mutate],
    );
    const onRemoveExpense = React.useCallback(
        (key: string) =>
            mutate((p) => ({
                ...p,
                expenses: p.expenses.filter((e) => e.key !== key),
            })),
        [mutate],
    );
    const onExpenseFieldChange = React.useCallback(
        (key: string, field: 'label' | 'amount', value: string) =>
            mutate((p) => ({
                ...p,
                expenses: p.expenses.map((e) =>
                    e.key === key ? { ...e, [field]: value } : e,
                ),
            })),
        [mutate],
    );

    const summary = React.useMemo(() => computeSummary(state), [state]);
    const dirty = React.useMemo(() => isDirty(state), [state]);
    const importError = React.useMemo(
        () => validateManualImport(state),
        [state],
    );

    // First save POSTs (the server assigns the id, lifted to the URL via
    // `onDraftCreated`); every save after that PUTs a full replace. Do NOT
    // retry on 400 — the server's validation text is surfaced as-is.
    const onSaveDraft = React.useCallback(async () => {
        // Serialise against the other draft mutations: a save racing an
        // in-flight DELETE could resurrect the draft the user just
        // discarded (and a double-click would double-POST). A save racing
        // the final import could rewrite a draft mid-transition.
        if (
            createDraftMutation.isPending ||
            updateDraftMutation.isPending ||
            deleteDraftMutation.isPending ||
            importMutation.isPending
        ) {
            return;
        }
        setStatus(null);
        setSaveError(null);
        const request = buildManualImportRequest(state, method);
        const result = draftId
            ? await updateDraftMutation.mutateAsync({
                  id: draftId,
                  body: request,
              })
            : await createDraftMutation.mutateAsync(request);
        if (!result.success) {
            setSaveError({
                labelKey: 'purchases.manualImport.errors.saveDraftFailed',
                message: result.error.message ?? 'Unknown error.',
            });
            return;
        }
        if (!draftId) {
            setDraftId(result.response.id);
            onDraftCreated?.(result.response.id);
        }
        setStatus('draftSaved');
    }, [
        createDraftMutation,
        deleteDraftMutation,
        draftId,
        importMutation,
        method,
        onDraftCreated,
        state,
        updateDraftMutation,
    ]);

    // DELETE (soft) the persisted draft, then let the page navigate away.
    // Only offered once a draft exists — an unsaved form has nothing to
    // discard on the server.
    const onDiscardDraft = React.useCallback(async () => {
        if (!draftId) return;
        // Serialise against saves and the final import: deleting while
        // either is in flight could drop the draft the user just saved or
        // race its in-place transition (server ordering decides).
        if (
            createDraftMutation.isPending ||
            updateDraftMutation.isPending ||
            deleteDraftMutation.isPending ||
            importMutation.isPending
        ) {
            return;
        }
        setSaveError(null);
        const result = await deleteDraftMutation.mutateAsync(draftId);
        if (!result.success) {
            setSaveError({
                labelKey: 'purchases.manualImport.errors.discardDraftFailed',
                message: result.error.message ?? 'Unknown error.',
            });
            return;
        }
        // The server copy is gone — a later save must POST a fresh draft,
        // not PUT the deleted id (matters when the page doesn't navigate
        // away, e.g. the Excel file-import flow).
        setDraftId(null);
        onDraftDiscarded?.();
    }, [
        createDraftMutation,
        deleteDraftMutation,
        draftId,
        importMutation,
        onDraftDiscarded,
        updateDraftMutation,
    ]);

    // Final import (`POST /purchases/import`, bos-api BOS-57). With a saved
    // draft in play its id rides along so the Draft transitions in place
    // (same id, falls off the drafts list). Do NOT retry on 400 — the
    // server's validation text is surfaced as-is.
    const onImport = React.useCallback(async () => {
        if (validateManualImport(state) !== null) return;
        // Serialise against the draft mutations — importing mid-save could
        // transition a half-written draft; mid-discard, a deleted one.
        if (
            createDraftMutation.isPending ||
            updateDraftMutation.isPending ||
            deleteDraftMutation.isPending ||
            importMutation.isPending
        ) {
            return;
        }
        setStatus(null);
        setSaveError(null);
        const result = await importMutation.mutateAsync({
            ...buildManualImportRequest(state, method),
            draftId,
        });
        if (!result.success) {
            setSaveError({
                labelKey: 'purchases.manualImport.errors.importFailed',
                message: result.error.message ?? 'Unknown error.',
            });
            return;
        }
        setStatus('imported');
        onImported?.();
    }, [
        createDraftMutation,
        deleteDraftMutation,
        draftId,
        importMutation,
        method,
        onImported,
        state,
        updateDraftMutation,
    ]);

    const isSavingDraft =
        createDraftMutation.isPending || updateDraftMutation.isPending;
    const isDiscardingDraft = deleteDraftMutation.isPending;
    const isImporting = importMutation.isPending;

    return {
        state,
        status,
        saveError,
        draftId,
        supplierOptions,
        branchOptions,
        categoryOptions,
        brandOptions,
        variationAxesByCategory,
        summary,
        // Save, Discard and Import are mutually exclusive while any is in
        // flight — racing PUT/DELETE/import calls on the same draft would
        // leave whatever the server happened to apply last.
        canSaveDraft:
            dirty && !isSavingDraft && !isDiscardingDraft && !isImporting,
        // A server draft exists — the in-flight state is `isDiscardingDraft`
        // so the button stays mounted (pending) while the DELETE runs; the
        // view disables it while a save/import is in flight.
        canDiscardDraft: draftId !== null,
        isSavingDraft,
        isDiscardingDraft,
        isImporting,
        canImport: importError === null && !isSavingDraft && !isDiscardingDraft,
        importError,
        onSupplierChange,
        onBranchChange,
        onInvoiceNumberChange,
        onDateChange,
        onAddExistingLine,
        onAddNewLine,
        onAddPreparedLine,
        onRemoveLine,
        onLineFieldChange,
        onPricingModeChange,
        onLineCategoryChange,
        onLineBrandChange,
        onLineOptionChange,
        onLineUnitChange,
        onLineVatChange,
        onAddExpense,
        onRemoveExpense,
        onExpenseFieldChange,
        onSaveDraft,
        onDiscardDraft,
        onImport,
    };
};

/** Everything the view tree needs, shared by the decomposed components. */
export type ManualImportForm = ReturnType<typeof useManualImport>;
